import { Button, Card, Grid, makeStyles, Typography } from "@material-ui/core";
import clsx from "clsx";
import ContractExecutiveBudget from "components/Contract/ContractExecutiveBudget";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { formatNumberToMonetary } from "utils";
import { lang } from "../../i18nLang";
import { IndicatorsStore as Store } from "../../store";
import { PageLoading } from "../UI/Page";
import IndicatorChart from "./IndicatorChart";
import IndicatorsAddAndCancelsModal from "./IndicatorsAddAndCancelsModal";

const useStyles = makeStyles((theme) => ({
  container: {
    height: "calc(100vh - 143px)",
    padding: theme.spacing(4, 8),
    overflowY: "auto",
  },
  card: {
    width: "100%",
    height: "fit-content",
    maxHeight: 500,
    padding: theme.spacing(2, 4),
    paddingBottom: 0,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    "& > p, & > button": {
      margin: theme.spacing(1, 0),
    },
  },
  cardNoHasBudget: {
    height: 500,
  },
  title: { fontWeight: 700 },
  text: {
    color: theme.palette.gray[400],
    textAlign: "center",
    fontStyle: "italic",
    fontWeight: 500,
  },
  fullHeight: {
    height: "100%",
  },
  maxHeightFitContent: {
    maxHeight: "fit-content !important",
  },
  totalsContainer: {
    margin: "1.5rem 0",
    maxHeight: "fit-content",
  },
  totalCard: {
    maxHeight: "fit-content",

    "&:first-child": {
      paddingRight: "0.5rem",
    },
    "&:last-child": {
      paddingLeft: "0.5rem",
    },
    "& > div": {
      border: `1px solid ${theme.palette.gray[300]}`,
    },
    "& .title": {
      fontSize: "1rem",
      fontWeight: 500,
      color: theme.palette.gray[500],
    },
    "& .value": {
      fontSize: "1.5rem",
      fontWeight: 700,
      color: theme.palette.gray[900],
    },
  },
  buttonDetail: {
    marginBottom: "1rem !important",
    width: "fit-content",
  },
  loading: {
    minHeight: 460,
  },
}));

const ContractChart = ({ loading, data }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card}>
      <Typography variant="h6" className={classes.title}>
        {lang(`indicatorsPage.charts.contract.title`)}
      </Typography>
      {loading ? (
        <PageLoading className={classes.loading} />
      ) : (
        <IndicatorChart
          title={lang(`indicatorsPage.charts.contract.chartLabel`)}
          type="contract"
          parameters={data}
        />
      )}
    </Card>
  );
};

const BudgetChart = ({ loading, data }) => {
  const classes = useStyles();
  return (
    <Card className={classes.card}>
      <Typography variant="h6" className={classes.title}>
        {lang(`indicatorsPage.charts.budget.title`)}
      </Typography>
      {loading ? (
        <PageLoading className={classes.loading} />
      ) : (
        <IndicatorChart
          title={lang(`indicatorsPage.charts.budget.chartLabel`)}
          type="budget"
          parameters={data}
        />
      )}
    </Card>
  );
};

const Indicators = observer(() => {
  const classes = useStyles();
  const [loadingContractChart, setLoadingContractChart] = useState(true);
  const [loadingBudgetChart, setLoadingBudgetChart] = useState(true);
  const [loadingAddsAndCancels, setLoadingAddsAndCancels] = useState(true);
  const [openExecutiveBudget, setOpenExecutiveBudget] = useState(false);
  const [closeBudgetChart, setCloseBudgetChart] = useState(false);
  const [openAddsAndCancelsDetails, setOpenAddsAndCancelsDetails] =
    useState(false);

  const {
    contractIndicators,
    budgetIndicators,
    hasExecutiveBudget,
    additivesTotal,
    cancellationsTotal,
  } = Store;

  useEffect(() => {
    Store.setCanFetchBudgetTotals(true);
    Store.getContractTotals().finally(() => setLoadingContractChart(false));
    Store.getExecutiveTotalsIfAuthenticated().finally(() =>
      setLoadingBudgetChart(false)
    );
    Store.getContractsRowsMoviments().finally(() =>
      setLoadingAddsAndCancels(false)
    );

    return () => {
      Store.setCanFetchBudgetTotals(false);
    };
  }, []);

  const handleOpenExecutiveBudgetModal = () => {
    setOpenExecutiveBudget(true);
  };

  const handleCloseExecutiveBudgetModal = () => {
    setOpenExecutiveBudget(false);
  };

  const handleClickClose = () => {
    setCloseBudgetChart(true);
  };

  const getContractCardWidth = () => {
    if (closeBudgetChart) return 6;
    return hasExecutiveBudget ? 6 : 10;
  };

  const getBudgetCardWidth = () => {
    if (closeBudgetChart) return false;
    return hasExecutiveBudget ? 6 : 2;
  };

  const handleOpenAddsAndCancelsDetailsModal = () => {
    setOpenAddsAndCancelsDetails(true);
  };

  const handleCloseAddsAndCancelsDetailsModal = () => {
    setOpenAddsAndCancelsDetails(false);
  };

  return (
    <>
      <Grid
        container
        className={classes.container}
        justifyContent="flex-start"
        alignItems="flex-start"
        spacing={6}
      >
        <Grid
          className={classes.maxHeightFitContent}
          container
          item
          xs={12}
          lg={getContractCardWidth()}
        >
          <ContractChart
            loading={loadingContractChart}
            data={contractIndicators}
          />
        </Grid>
        {!closeBudgetChart && (
          <Grid
            className={clsx({
              [classes.maxHeightFitContent]: hasExecutiveBudget,
            })}
            container
            item
            xs={12}
            lg={getBudgetCardWidth()}
          >
            {hasExecutiveBudget && (
              <BudgetChart
                loading={loadingBudgetChart}
                data={budgetIndicators}
              />
            )}
            {!hasExecutiveBudget && (
              <Card className={clsx(classes.card, classes.cardNoHasBudget)}>
                <Typography variant="body1" className={classes.text}>
                  {lang("indicatorsPage.noLink")}
                </Typography>
                <Button
                  color="primary"
                  variant="contained"
                  onClick={handleOpenExecutiveBudgetModal}
                >
                  {lang("link")}
                </Button>
                <Button color="primary" onClick={handleClickClose}>
                  {lang("close")}
                </Button>
              </Card>
            )}
          </Grid>
        )}
        <Grid
          className={classes.maxHeightFitContent}
          container
          item
          xs={12}
          lg={6}
        >
          <Card className={clsx(classes.card, classes.maxHeightFitContent)}>
            <Typography variant="h6" className={classes.title}>
              {lang(`indicatorsPage.addsAndCancels.title`)}
            </Typography>
            {loadingAddsAndCancels && <PageLoading />}
            {!loadingAddsAndCancels && (
              <>
                <Grid
                  className={classes.totalsContainer}
                  container
                  item
                  xs={12}
                >
                  <Grid className={classes.totalCard} item xs={12} lg={6}>
                    <Grid container direction="column" alignItems="center">
                      <Typography className="title">
                        {lang(`indicatorsPage.addsAndCancels.adds`)}
                      </Typography>
                      <Typography className="value">
                        {formatNumberToMonetary(additivesTotal)}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Grid className={classes.totalCard} item xs={12} lg={6}>
                    <Grid container direction="column" alignItems="center">
                      <Typography className="title">
                        {lang(`indicatorsPage.addsAndCancels.cancels`)}
                      </Typography>
                      <Typography className="value">
                        {formatNumberToMonetary(cancellationsTotal)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Grid>
                <Button
                  className={classes.buttonDetail}
                  color="primary"
                  variant="outlined"
                  onClick={handleOpenAddsAndCancelsDetailsModal}
                >
                  {lang("indicatorsPage.addsAndCancels.details")}
                </Button>
              </>
            )}
          </Card>
        </Grid>
      </Grid>
      <ContractExecutiveBudget
        open={openExecutiveBudget}
        onClose={handleCloseExecutiveBudgetModal}
      />
      <IndicatorsAddAndCancelsModal
        open={openAddsAndCancelsDetails}
        onClose={handleCloseAddsAndCancelsDetailsModal}
      />
    </>
  );
});

export default Indicators;
